import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import clsx from 'clsx';
import { notify } from '../../app';
import Button from '../Button';

export type Row = Record<string, string>;

type FileReaderProps = {
  onChange?: (rows: Row[] | null, file: File | null) => void;
  value?: string;
  file?: File | null;
  className?: string;
  label?: string;
  requiredColumns?: string[];
  viewMode?: 'drag-drop' | 'upload-button';
  uploadButtonText?: string;
  uploadButtonClassName?: string;
};

const FileReaderComponent: React.FC<FileReaderProps> = ({
  onChange,
  className,
  label = 'Upload CSV or Excel file',
  requiredColumns = ['company_name', 'url'],
  viewMode = 'drag-drop',
  uploadButtonText = 'Upload File',
  uploadButtonClassName,
}) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateColumns = (headers: string[]): boolean => {
    if (!requiredColumns || requiredColumns.length === 0) {
      return true;
    }

    const normalizedHeaders = headers.map(h => h.toLowerCase());
    const normalizedRequiredColumns = requiredColumns.map(col =>
      col.toLowerCase()
    );

    return normalizedRequiredColumns.some(requiredCol =>
      normalizedHeaders.includes(requiredCol)
    );
  };

  const getMissingColumnsMessage = (headers: string[]): string => {
    const normalizedHeaders = headers.map(h => h.toLowerCase());
    const normalizedRequiredColumns = requiredColumns.map(col =>
      col.toLowerCase()
    );

    const missingColumns = normalizedRequiredColumns.filter(
      requiredCol => !normalizedHeaders.includes(requiredCol)
    );

    if (missingColumns.length === 0) return '';

    return `File must contain at least one of these columns: ${missingColumns.join(', ')}`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setFileName(file.name);

    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'csv') {
      Papa.parse<Row>(file, {
        header: true,
        skipEmptyLines: true,
        complete: result => {
          setIsUploading(false);

          const headers = Object.keys(result.data[0] || {});
          const isValid = validateColumns(headers);

          if (!isValid) {
            const message = getMissingColumnsMessage(headers);
            notify.error(message || 'File does not contain required columns.');
            handleClear();
            return;
          }

          onChange?.(result.data, file);
        },
        error: error => {
          setIsUploading(false);
          console.error('CSV Parse Error:', error);
          handleClear();
        },
      });
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json<Row>(sheet);

          setIsUploading(false);

          const headers = Object.keys(json[0] || {});
          const isValid = validateColumns(headers);

          if (!isValid) {
            const message = getMissingColumnsMessage(headers);
            notify.error(message || 'File does not contain required columns.');
            handleClear();
            return;
          }

          onChange?.(json, file);
        } catch (error) {
          setIsUploading(false);
          console.error('Excel Parse Error:', error);
          notify.error(
            'Failed to parse Excel file. Please check the file format.'
          );
          handleClear();
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setIsUploading(false);
      notify.error(
        'Unsupported file format. Please upload a CSV or Excel file.'
      );
      handleClear();
    }
  };

  const handleClear = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName(null);
    setIsUploading(false);
    onChange?.(null, null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (viewMode === 'upload-button') {
    return (
      <div className={clsx('flex', className)}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv, .xlsx, .xls"
          onChange={handleFileUpload}
          className="hidden"
        />

        {fileName ? (
          <div className="flex items-center gap-4 w-full bg-gray-100 rounded-md p-2">
            <input
              type="text"
              value={fileName}
              disabled
              className="flex-1 border border-gray-300 rounded px-2 py-2 mr-2 text-small bg-gray-100"
            />
            <button
              type="button"
              onClick={handleClear}
              className="text-red-500 text-small hover:underline flex-shrink-0"
              disabled={isUploading}
            >
              Clear
            </button>
          </div>
        ) : (
          <div className="flex justify-end w-full">
            <Button
              size="empty"
              type="empty"
              onClick={handleUploadClick}
              disabled={isUploading}
              className={clsx('font-medium', uploadButtonClassName)}
              label={isUploading ? 'Uploading...' : uploadButtonText}
              loading={isUploading}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={clsx('p-6', className)}>
      {!fileName && <span className="text-text py-2">{label}</span>}
      <div
        className={clsx(
          'w-full flex items-center',
          'justify-center rounded-md p-4',
          `${!fileName ? 'bg-gray-100 border-dashed border border-border min-h-[150px]' : ''}`
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv, .xlsx, .xls"
          onChange={handleFileUpload}
          className="hidden"
        />

        {fileName ? (
          <div className="flex items-center justify-center gap-4">
            <input
              type="text"
              value={fileName}
              disabled
              className="w-full border border-gray-300 rounded px-3 py-2 text-small bg-gray-100"
            />
            <button
              type="button"
              onClick={handleClear}
              className="text-red-500 text-small hover:underline"
              disabled={isUploading}
            >
              Clear
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={isUploading}
            className="text-small text-blue-600 underline disabled:text-gray-400"
          >
            {isUploading ? 'Uploading...' : label}
          </button>
        )}
      </div>
    </div>
  );
};

export default FileReaderComponent;
